import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Printer, FileSpreadsheet, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { inv, INV_API } from '../../lib/invApi';
import { toast } from 'sonner';

const num = (n) => (Number.isInteger(n) ? n : Number(n || 0).toFixed(2));
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Company Reorder List — Suggested Order = Company Min − Shop + Warehouse On Hand (> 0 only).
// Van estimates and loadout differences are never part of the purchase quantity.
const InventoryReorder = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const createSheet = async (supplier) => {
    try {
      const { data: o } = await inv.post('/supplier-orders', { supplier, from_reorder: true });
      toast.success(`${o.order_number} created for ${supplier}`);
      navigate(`/admin/inventory-hub/supplier-orders?open=${o.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create order sheet'); }
  };
  useEffect(() => { inv.get('/reorder').then(({ data }) => setData(data)).catch(() => toast.error('Could not load reorder list')); }, []);

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${INV_API}/reorder`, { params: { format: 'csv' }, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `reorder-list-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (!data) return <div className="text-[#A3A3A3]">Loading…</div>;
  return (
    <div data-testid="inv-reorder-page" className="space-y-5 print:text-black">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3 print:text-black"><ShoppingCart className="w-7 h-7 text-[#D4AF37]" /> Reorder List</h2>
          <p className="text-[#A3A3A3] text-sm">Suggested Order = Company Minimum − Shop + Warehouse On Hand, only when above 0. Van stock is never counted.</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid="inv-reorder-print" variant="outline" onClick={() => window.print()} className="border-[#262626] text-white hover:bg-[#262626] hover:text-white"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button data-testid="inv-reorder-csv" variant="outline" onClick={downloadCsv} className="border-[#262626] text-white hover:bg-[#262626] hover:text-white"><FileSpreadsheet className="w-4 h-4 mr-1" />Export CSV</Button>
        </div>
      </div>
      <p data-testid="inv-reorder-summary" className="text-sm text-white">{data.total_items} item{data.total_items === 1 ? '' : 's'} to reorder · est. {money(data.est_value)}</p>
      {data.groups.length === 0 && <div className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center text-[#525252]" data-testid="inv-reorder-empty">Nothing is below its Company Minimum.</div>}
      {data.groups.map((g) => (
        <section key={g.supplier} data-testid={`inv-reorder-group-${g.supplier.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto print:break-inside-avoid">
          <div className="flex justify-between px-3 py-2 bg-[#0A0A0A] border-b border-[#262626]">
            <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wide">{g.supplier}</span>
            <span className="flex items-center gap-3"><span className="text-[#A3A3A3] text-xs">{g.count} item{g.count === 1 ? '' : 's'} · est. {money(g.est_value)}</span>
              <Button data-testid={`inv-reorder-create-sheet-${g.supplier.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} size="sm" variant="outline" onClick={() => createSheet(g.supplier)} className="h-7 border-[#262626] text-white hover:bg-[#262626] hover:text-white print:hidden"><ClipboardList className="w-3.5 h-3.5 mr-1" />Create Order Sheet</Button></span>
          </div>
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-[#A3A3A3] border-b border-[#262626]">
              {['Item', 'Trade', 'Supplier', 'Company Min', 'Shop + WH On Hand', 'Suggested Order'].map((h, i) => <th key={h} className={`px-3 py-2 font-medium ${i > 2 ? 'text-right' : 'text-left'}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {g.items.map((r) => (
                <tr key={r.item_id} data-testid={`inv-reorder-row-${r.item_id}`} className="border-b border-[#262626] last:border-0">
                  <td className="px-3 py-2 text-white">{r.item_name}{r.item_sku && <span className="text-[#525252] text-xs ml-2">{r.item_sku}</span>}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.trades.join(', ') || 'No Trade'}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.supplier || 'Supplier Not Assigned'}</td>
                  <td className="px-3 py-2 text-right text-[#A3A3A3]">{num(r.company_min)}</td>
                  <td className="px-3 py-2 text-right text-white">{num(r.controlled_on_hand)}</td>
                  <td data-testid={`inv-reorder-qty-${r.item_id}`} className="px-3 py-2 text-right text-[#D4AF37] font-bold">{num(r.suggested_order)} {r.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
};

export default InventoryReorder;
